import axios from 'axios'
import { msalInstance } from '../../main'
import { loginRequest } from '../authConfig'
import { getImpersonationToken, hasImpersonationToken } from './impersonationSession'
import { contracts as meContracts } from '../../../../shared/features/me.contracts'
import { contracts as platformContracts } from '../../../../shared/features/platform.contracts'
import { resetSessionQueries } from '../query/queryClient'
import { requestVanityConfig } from './vanityConfigRequest'
import { INTEGRATION_LIMIT_EVENT } from './integrationLimitEvent'

// Get configuration from Vite
const config = window.__APP_CONFIG__ || __APP_CONFIG__

// Flag to force token refresh on next request
let forceTokenRefresh = false

export const AUTH_LOGIN_REQUIRED_EVENT = 'ops:auth-login-required'
export const WORKSPACE_SETUP_REQUIRED_EVENT = 'ops:workspace-setup-required'
export { INTEGRATION_LIMIT_EVENT } from './integrationLimitEvent'

export function notifyAuthLoginRequired(reason, cause) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return
  window.dispatchEvent(new window.CustomEvent(AUTH_LOGIN_REQUIRED_EVENT, {
    detail: {
      reason,
      errorCode: cause?.errorCode || cause?.code || null,
    },
  }))
}

const apiClient = axios.create({
  baseURL: config.apiUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// The authenticated shell used to mount its gates serially: identity first,
// then setup, then entitlements and timezone. Keep the existing endpoints and
// their fail-closed owners, but start the three independent reads together and
// let later mounts reuse the exact same Axios promises. Timezone is carried by
// /api/users/me, so it no longer needs a fourth request.
//
// The entitlement read is unforced: a forced refresh happens only on a manual
// refresh and once when an admin opens the License page, never on bootstrap,
// focus or interval.
const SESSION_BOOTSTRAP_TTL_MS = 120000
const SESSION_BOOTSTRAP_URLS = [
  meContracts.usersMe.build(),
  platformContracts.workspaceSetupState.build(),
  platformContracts.entitlementsState.build(),
]
const sessionBootstrapUrlSet = new Set(SESSION_BOOTSTRAP_URLS)
const sessionBootstrapResponses = new Map()
let sessionBootstrapKey = null
const uncachedGet = apiClient.get.bind(apiClient)

function cachedBootstrapGet(url, requestConfig) {
  const useCache = requestConfig?.sessionBootstrapCache !== false
  if (!useCache || !sessionBootstrapUrlSet.has(url)) {
    return uncachedGet(url, requestConfig)
  }
  const now = Date.now()
  const existing = sessionBootstrapResponses.get(url)
  if (existing && existing.expiresAt > now) return existing.promise
  const promise = uncachedGet(url, requestConfig)
  sessionBootstrapResponses.set(url, { promise, expiresAt: now + SESSION_BOOTSTRAP_TTL_MS })
  return promise
}

apiClient.get = cachedBootstrapGet

export function clearSessionBootstrap() {
  sessionBootstrapResponses.clear()
  sessionBootstrapKey = null
}

export function primeSessionBootstrap(nextSessionKey = 'authenticated') {
  if (sessionBootstrapKey !== nextSessionKey) {
    sessionBootstrapResponses.clear()
    sessionBootstrapKey = nextSessionKey
  }
  // allSettled installs rejection handlers immediately; the owning gates still
  // receive and render each original failure when they consume the promise.
  return Promise.allSettled(SESSION_BOOTSTRAP_URLS.map((url) => apiClient.get(url)))
}

// Gate every API call on discovery so cross-session state can't leave
// the client pinned to a deleted vanity host. The canonical baked apiUrl
// always works as fallback, so the first call is only delayed by the
// admin-side discovery round trip.
let apiReady = null
const VANITY_PROBE_TIMEOUT_MS = 5000

async function vanityApiReachable(apiUrl) {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), VANITY_PROBE_TIMEOUT_MS)

  try {
    const response = await fetch(`${apiUrl}/version`, {
      signal: controller.signal,
    })
    return response.ok
  } catch {
    return false
  } finally {
    window.clearTimeout(timeoutId)
  }
}

const VANITY_PROBE_CACHE_KEY = 'ops:vanity-probe'
const VANITY_PROBE_FAILURE_TTL_MS = 5 * 60 * 1000

// The /version probe decision, so a broken vanity host stalls only one cold
// load: a success holds for the session, a failure for 5 minutes. Explicit
// refreshes ({ fresh: true }) always probe again.
function readVanityProbeCache() {
  try {
    const raw = sessionStorage.getItem(VANITY_PROBE_CACHE_KEY)
    if (!raw) return null
    const entry = JSON.parse(raw)
    if (!entry || typeof entry.url !== 'string' || typeof entry.ok !== 'boolean' || typeof entry.at !== 'number') {
      return null
    }
    if (!entry.ok && Date.now() - entry.at > VANITY_PROBE_FAILURE_TTL_MS) return null
    return entry
  } catch {
    return null
  }
}

function writeVanityProbeCache(url, ok) {
  try {
    sessionStorage.setItem(VANITY_PROBE_CACHE_KEY, JSON.stringify({ url, ok, at: Date.now() }))
  } catch {
    // A full or blocked storage must not break discovery.
  }
}

function applyVanityProbeResult(discoveredApiUrl, ok) {
  if (ok) {
    apiClient.defaults.baseURL = discoveredApiUrl
    return
  }
  // The discovered vanity API host isn't reachable from this browser —
  // fall back to the canonical URL so the app keeps working, but say so:
  // a silent fallback hides a genuinely broken vanity setup.
  console.warn(`[vanity] discovered API host ${discoveredApiUrl} is not reachable; using canonical ${config.apiUrl}. Check the vanity hostname's DNS/cert/Access config.`)
  apiClient.defaults.baseURL = config.apiUrl
}

async function runDiscovery({ fresh = false } = {}) {
  try {
    const data = await requestVanityConfig({ fresh })
    const discoveredApiUrl = typeof data.apiUrl === 'string' ? data.apiUrl.trim() : ''
    if (!discoveredApiUrl || discoveredApiUrl === config.apiUrl) {
      apiClient.defaults.baseURL = config.apiUrl
      return
    }

    if (!fresh) {
      const cached = readVanityProbeCache()
      if (cached && cached.url === discoveredApiUrl) {
        applyVanityProbeResult(discoveredApiUrl, cached.ok)
        return
      }
    }

    const ok = await vanityApiReachable(discoveredApiUrl)
    writeVanityProbeCache(discoveredApiUrl, ok)
    applyVanityProbeResult(discoveredApiUrl, ok)
  } catch { /* keep current config */ }
}

async function waitForReady() {
  if (apiReady) await apiReady
}

// Discover vanity API URL at runtime so newly activated vanity
// hostnames work without redeploying the frontend. Always calls the
// canonical (baked) worker URL — never a cached vanity URL — so
// discovery works even after a vanity hostname is deleted. Callers can
// await the returned promise to know discovery has completed.
let discoveryInFlight = null
export function refreshVanityConfig({ fresh = false } = {}) {
  // Coalesce concurrent callers (module-load kickoff + a settings-page
  // refresh, say) onto one discovery pass, so two overlapping runs can't
  // race on apiClient.defaults.baseURL.
  if (discoveryInFlight) return discoveryInFlight
  discoveryInFlight = runDiscovery({ fresh }).finally(() => { discoveryInFlight = null })
  apiReady = discoveryInFlight
  return apiReady
}

// Kick off initial discovery at module load.
refreshVanityConfig()

async function acquireIdTokenUncached(forceRefreshRequested = false) {
  // Impersonation mode: the admin worker handed off an opaque imp_*
  // token in the URL hash, captured into sessionStorage by main.jsx.
  // Always prefer it — never let MSAL silent-acquire swap the
  // impersonation identity for the operator's own MSAL account.
  const impToken = getImpersonationToken()
  if (impToken) return impToken

  // Assigned during main.jsx bootstrap, once the Entra identifiers resolve —
  // so it is null until then, and stays null for a customer whose admin has
  // not yet granted consent. Nothing should reach here first (the app mounts
  // inside that bootstrap), but a null deref would read as an opaque TypeError.
  if (!msalInstance) {
    console.warn('[Auth] MSAL is not initialised; request will go unauthenticated')
    return null
  }

  try {
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length === 0) {
      console.warn('[Auth] No accounts found, request will fail authentication')
      return null
    }

    const request = {
      scopes: loginRequest.scopes,
      account: accounts[0],
      forceRefresh: forceRefreshRequested,
    }
    const response = await msalInstance.acquireTokenSilent(request)
    return response.idToken
  } catch (error) {
    console.error('[Auth Token Error]', error)
    if (error.errorCode === 'interaction_required' || error.errorCode === 'consent_required') {
      notifyAuthLoginRequired('silent_token_requires_interaction', error)
      return null
    }

    console.error('[Auth] Token acquisition failed, request will fail authentication')
    return null
  }
}

/**
 * Scope every agent blueprint exposes: the delegated permission the console
 * acquires so the worker can mint the person's agent token on-behalf-of the
 * person, one blueprint per area.
 */
export const AGENT_BLUEPRINT_SCOPE_SUFFIX = 'access_as_user'

/**
 * Silently acquire the person's token for one area blueprint
 * (api://<blueprint>/access_as_user). SILENT ONLY: never pops interaction,
 * never logs the token. Resolves `{ assertion }`, or `{ error }` carrying the
 * real reason the console could not get it (Microsoft's own error code and
 * text, e.g. AADSTS65001 when the area is not approved for the console) —
 * that area is then not usable for this person, and the reason travels to
 * the worker so a refusal can name it.
 */
export async function acquireAgentAssertion(blueprintClientId) {
  const blueprint = typeof blueprintClientId === 'string' ? blueprintClientId.trim() : ''
  if (!blueprint) return { error: 'No blueprint app id for this area' }
  if (typeof getImpersonationToken === 'function' && getImpersonationToken()) {
    return { error: 'A support impersonation session carries no Microsoft sign-in' }
  }
  if (!msalInstance) return { error: 'Microsoft sign-in is not initialised in this console' }
  const accounts = msalInstance.getAllAccounts()
  if (!accounts || accounts.length === 0) return { error: 'No Microsoft account is signed in to this console' }
  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: [`api://${blueprint}/${AGENT_BLUEPRINT_SCOPE_SUFFIX}`],
      account: accounts[0],
    })
    const token = typeof response?.accessToken === 'string' ? response.accessToken.trim() : ''
    return token ? { assertion: token } : { error: 'Microsoft returned no access token for this area' }
  } catch (error) {
    const code = typeof error?.errorCode === 'string' && error.errorCode ? `${error.errorCode}: ` : ''
    const message = typeof error?.errorMessage === 'string' && error.errorMessage
      ? error.errorMessage
      : error instanceof Error ? error.message : String(error)
    return { error: `${code}${message}`.trim() || 'Silent Microsoft sign-in failed' }
  }
}

let idTokenInFlight = null

export function acquireIdToken(forceRefreshRequested = false) {
  if (forceRefreshRequested) return acquireIdTokenUncached(true)
  if (idTokenInFlight) return idTokenInFlight
  const request = acquireIdTokenUncached(false)
  idTokenInFlight = request
  const clearInFlight = () => {
    if (idTokenInFlight === request) idTokenInFlight = null
  }
  request.then(clearInFlight, clearInFlight)
  return request
}

export async function apiFetch(url, options = {}) {
  await waitForReady()
  const baseUrl = apiClient.defaults.baseURL || config.apiUrl
  const resolvedUrl = url.startsWith('http://') || url.startsWith('https://')
    ? url
    : `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`

  const attempt = async (forceRefreshRequested = false) => {
    const headers = new globalThis.Headers(options.headers || {})
    const token = await acquireIdToken(forceRefreshRequested)
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    if (options.body != null && !headers.has('Content-Type') && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json')
    }

    return fetch(resolvedUrl, {
      ...options,
      headers,
    })
  }

  let response = await attempt(forceTokenRefresh)
  forceTokenRefresh = false
  if (response.status === 401) {
    response = await attempt(true)
  }
  return response
}

/** The absolute address of an API path, resolved as apiFetch resolves it (vanity host included). */
export async function resolveApiUrl(path) {
  await waitForReady()
  const baseUrl = apiClient.defaults.baseURL || config.apiUrl
  return new URL(`${baseUrl}${path.startsWith('/') ? path : `/${path}`}`, window.location.href).href
}

/** Fetch an explicitly public Worker route through the same vanity discovery
 * and configured API origin as authenticated calls, without acquiring or
 * attaching a user token. */
export async function publicApiFetch(url, options = {}) {
  await waitForReady()
  const baseUrl = apiClient.defaults.baseURL || config.apiUrl
  const resolvedUrl = url.startsWith('http://') || url.startsWith('https://')
    ? url
    : `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`
  return fetch(resolvedUrl, options)
}

/**
 * Force the next API request to get a fresh token from Azure
 * Call this after role changes to pick up new permissions
 */
export function forceRefreshToken() {
  clearSessionBootstrap()
  resetSessionQueries()
  forceTokenRefresh = true
}

// Request interceptor to add JWT token
apiClient.interceptors.request.use(
  async (config) => {
    await waitForReady()
    try {
      const shouldForceRefresh = forceTokenRefresh
      forceTokenRefresh = false
      const token = await acquireIdToken(shouldForceRefresh)
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      throw error
    }

    if (window.__APP_CONFIG__?.debugMode) {
      console.log('[API Request]', config.method.toUpperCase(), config.url, config)
    }
    return config
  },
  (error) => {
    console.error('[API Request Error]', error)
    return Promise.reject(error)
  }
)

// Response interceptor — retry once on 401 with a forced token refresh
apiClient.interceptors.response.use(
  (response) => {
    if (window.__APP_CONFIG__?.debugMode) {
      console.log('[API Response]', response.status, response.config.url, response.data)
    }
    return response
  },
  async (error) => {
    const originalRequest = error.config
    // Impersonation session ended — clear the token and notify the
    // tab. Do NOT trigger MSAL silent-refresh; the operator is logged
    // in elsewhere and a re-launch from admin is the only valid way
    // to get a new impersonation token. The dashboard worker's
    // `errorResponse` helper packs codes under `.details.code`.
    if (error.response?.status === 401 && error.response?.data?.details?.code === 'impersonation_expired') {
      clearSessionBootstrap()
      resetSessionQueries()
      sessionStorage.removeItem('impersonationToken')
      window.dispatchEvent(new Event('impersonation:ended'))
      return Promise.reject(error)
    }
    if (error.response?.status === 401 && !originalRequest._retry && !hasImpersonationToken()) {
      originalRequest._retry = true
      try {
        if (!msalInstance) throw new Error('no_accounts')
        const accounts = msalInstance.getAllAccounts()
        if (accounts.length === 0) throw new Error('no_accounts')
        const response = await msalInstance.acquireTokenSilent({
          scopes: loginRequest.scopes,
          account: accounts[0],
          forceRefresh: true,
        })
        originalRequest.headers.Authorization = `Bearer ${response.idToken}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        console.error('[Auth] Token refresh failed; top-level auth owner will handle login', refreshError)
        notifyAuthLoginRequired('forced_token_refresh_failed', refreshError)
        return Promise.reject(error)
      }
    }
    // WP4: centralized handler for per-app billing entitlement
    // denials. The server includes the missing keys in
    // response.data.error already, so callsites that display
    // `err.response?.data?.error || 'fallback'` get the right
    // non-generic toast for free. This block just guarantees the
    // friendly text is present even if a future server change drops
    // the keys from the top-level message, and adds a debug log.
    const data = error.response?.data
    if (error.response?.status === 423 && data?.details?.code === 'SETUP_REQUIRED') {
      window.dispatchEvent(new Event(WORKSPACE_SETUP_REQUIRED_EVENT))
    }
    // The server's message already names the License page; the event lets the
    // shell show a notice that links straight to it, whichever page asked.
    if (error.response?.status === 403 && data?.details?.code === 'LICENSE_INTEGRATION_LIMIT_EXCEEDED') {
      window.dispatchEvent(new window.CustomEvent(INTEGRATION_LIMIT_EVENT, {
        detail: { used: data.details.current_integrations, allowed: data.details.max_integrations },
      }))
    }
    if (error.response?.status === 403 && data?.details?.code === 'APP_ENTITLEMENT_REQUIRED') {
      const missing = Array.isArray(data.details.missing_entitlements)
        ? data.details.missing_entitlements
        : []
      const friendly = missing.length > 0
        ? `This action requires entitlements not active on your plan: ${missing.join(', ')}`
        : 'This action requires entitlements not active on your plan'
      // Replace the top-level error message so existing handlers
      // render it without per-callsite changes.
      data.error = friendly
      if (window.__APP_CONFIG__?.debugMode) {
        console.warn('[API APP_ENTITLEMENT_REQUIRED]', {
          app_code: data.details.app_code,
          missing_entitlements: missing,
        })
      }
    }
    if (window.__APP_CONFIG__?.debugMode) {
      console.error('[API Response Error]', error.response || error)
    }
    return Promise.reject(error)
  }
)

export default apiClient
